// The client side of /api/monitors (BUILD-BRIEF-R2 §8) — owned by the `signals` agent.
import { qs, request } from './client.js';
import type { ListResponse, Monitor, MonitorKind, MonitorPreset, SubjectKind } from '../../shared/types.js';

export function listMonitors(
  params: { subject_kind?: SubjectKind; subject_id?: number; kind?: MonitorKind; status?: Monitor['status'] } = {},
): Promise<ListResponse<Monitor>> {
  return request(`/monitors${qs(params)}`);
}

export interface CreateMonitorInput {
  kind: MonitorKind;
  target: string;
  subject_kind?: SubjectKind | null;
  subject_id?: number | null;
  cadence_hours?: number;
}

export function createMonitor(input: CreateMonitorInput): Promise<Monitor> {
  return request('/monitors', { method: 'POST', body: JSON.stringify(input) });
}

export function updateMonitor(id: number, patch: Partial<CreateMonitorInput> & { status?: Monitor['status'] }): Promise<Monitor> {
  return request(`/monitors/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
}

export function deleteMonitor(id: number): Promise<{ ok: true }> {
  return request(`/monitors/${id}`, { method: 'DELETE' });
}

/**
 * What "Run now" reports back: the run was CLAIMED, not finished. A sweep is minutes of politely
 * spaced requests, so the server answers 202 with the run row it claimed and works on; the
 * outcome arrives in `monitors.last_outcome` when the sweep lands. A 409 (already in flight)
 * comes back as an ApiError, which is the truthful answer to a second impatient click.
 */
export interface MonitorRunResponse {
  monitor_id: number;
  run_id: number | null;
  queued: boolean;
  refused_reason?: string;
  monitor: Monitor;
}

export function runMonitor(id: number): Promise<MonitorRunResponse> {
  return request(`/monitors/${id}/run`, { method: 'POST' });
}

/** The one-click monitors — shipped disabled until the operator enables one. */
export function monitorPresets(): Promise<ListResponse<MonitorPreset>> {
  return request('/monitors/presets');
}

export function enablePreset(id: string): Promise<Monitor> {
  return request(`/monitors/presets/${encodeURIComponent(id)}/enable`, { method: 'POST' });
}
